import * as React from 'react';
import moment from 'moment';
import { Icon, Tag, Row, Col, Tooltip } from 'antd';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import withEximType from 'src/components/hoc/withEximType';
import classNames from 'classnames';
import './loading.css';
import { colorStatusMapping } from '../../../colorScheme/containerStatus';
import ReactTimeAgo from 'react-time-ago';
import { SHIPMENT_TYPE, STATUS_LABELS } from './ContainerUtils';
import { isValidArray } from 'src/Library/Helper';
import { CardData } from './Interfaces/Shipment.interface';
import { RouterProps, StylesProps } from 'src/Library/Types';
import { tokens } from 'src/design-system';
import { commonStyles } from 'src/Library/CommonStyles';
import VTSideDrawer from './VesselTracking/VTSideDrawer';
import { CTPermissions } from './Interfaces/TypesInShipmentTracking';

const styles = (theme: any) => ({
  success: {
    backgroundColor: tokens.colors.success,
    borderColor: tokens.colors.success,
    color: tokens.colors.bgWhite,
    textTransform: 'capitalize',
    marginLeft: tokens.spacing.md,
    fontSize: tokens.fontSize.caption,
    fontWeight: tokens.fontWeight.regular,
    borderRadius: '18px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  error: {
    backgroundColor: `${theme.errorColor}`,
    borderColor: `${theme.errorColor}`,
    color: tokens.colors.bgWhite,
    textTransform: 'capitalize',
    marginLeft: tokens.spacing.md,
    fontSize: tokens.fontSize.caption,
    fontWeight: tokens.fontWeight.regular,
    borderRadius: '18px',
  },
  delayed: {
    opacity: '100%',
    borderColor: '#F38755',
    color: tokens.colors.textTertiary,
    textTransform: 'capitalize',
    marginLeft: '0px',
    marginRight: tokens.spacing.ms,
    fontSize: tokens.fontSize.caption,
    fontWeight: tokens.fontWeight.regular,
    borderRadius: '18px',
  },
  containerCol: {
    paddingLeft: tokens.spacing.lg,
    alignSelf: 'flex-start',
    whiteSpace: 'pre-line',
  },
  containerColLabel: {
    fontWeight: tokens.fontWeight.regular,
    paddingBottom: '9px',
    color: tokens.colors.textTertiary,
  },
  parent: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '22px 22px 0 22px',
  },
  containerParent: {
    marginTop: '10px',
    padding: tokens.spacing.ms,
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.2)',
    backgroundColor: tokens.colors.bgWhite,
    cursor: 'pointer',
    background: tokens.colors.bgWhite,
    borderRadius: tokens.radius.md,
    paddingBottom: tokens.spacing.ms,
    marginBottom: tokens.spacing.ms,
  },
  cursorStyle: {
    cursor: 'pointer',
  },
  label: {
    paddingLeft: '10px',
  },
  col: {
    color: tokens.colors.textSecondary,
    fontSize: tokens.fontSize.small,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 150,
  },
  archived: {
    opacity: 0.7,
    filter: 'grayscale(100)',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    color: tokens.colors.success,
  },
  completionReason: {
    fontSize: tokens.fontSize.small,
    color: tokens.colors.textTertiary,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    bottom: 5,
    '& span[id=reason]': {
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      maxWidth: "400px",
      // width: "400px",
      overflow: "hidden",
      marginLeft: '5px',
    }
  },
  flexRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  flexCol: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  containerType: {
    color: tokens.colors.textTertiary,
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '17px',
  },
  tag: {
    maxWidth: '250px',
    display: 'flex',
    fontSize: tokens.fontSize.small,
    color: tokens.colors.textSecondary,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '17px',
  },
  icons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    position: 'absolute',
    top: 0,
    right: tokens.spacing.ms,
  },
  lastLocation: {
    textAlign: 'right',
    display: 'inline',
    fontSize: tokens.fontSize.small,
    color: tokens.colors.textTertiary,
    lineHeight: '17px',
  },
  status: {
    color: tokens.colors.textSecondary,
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '19px',
  },
  bookingNumber: {
    color: tokens.colors.textSecondary,
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '19px',
    maxWidth: '100%',
    ...commonStyles(theme).ellipses,
  },
  labelCol: {
    textAlign: 'right',
    display: 'inline',
    fontSize: tokens.fontSize.small,
    color: tokens.colors.textTertiary,
    lineHeight: '17px',
  },
  transitDays: {
    color: tokens.colors.textSecondary,
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '19px',
  },
});

const containerTypesKeyVal = {
  STANDARD: 'Standard',
  ISO_TANKS: 'Tank',
  REFRIGERATED: 'Refrigerated',
  OPEN_TOP: 'Open Top',
  FLAT_RACK: 'Flat Rack',
  HEAVY_DUTY: 'Heavy Duty',
  OTHER: 'Other',
};

const componentName = 'SUB_CONTAINER_CARD';

interface IProps extends StylesProps<ReturnType<typeof styles>>, RouterProps {
  item: CardData;
  isPublicLink: boolean;
  isContainerView: boolean;
  onClick: () => void;
  isHBL: () => boolean;
  handleEdit: () => void;
  handleDelete: (item: CardData) => void;
  style: any;
  index: number;
  container: CardData;
  permissions: CTPermissions;
  handleArchiveShipment: (items: CardData[]) => void;
  handleUnArchiveShipment: () => void;
  mainPageTabId: string;
}

const SubContainerCard = (props: IProps) => {

  const {
    item,
    match,
    classes,
    isPublicLink,
    onClick,
    style,
    index,
    handleEdit,
    handleDelete,
    isHBL,
    container,
    permissions,
    handleArchiveShipment,
    handleUnArchiveShipment,
    mainPageTabId,
  } = props;

  const renderTrackingHoldMessage = () => {
    if (!item?.trackAfterShipmentTaskApproval || item?.isTrackingPresent) {
      return null;
    }

    return (
      <div>
        <Icon type="info-circle" theme="filled" />
        <span style={{ marginLeft: 8, color: tokens.colors.error }}>
          {"Tracking is on hold"}
        </span>
      </div>
    );
  }

  const renderFetchingData = () => {
    if (item?.trackAfterShipmentTaskApproval) return false;
    if (item.isManuallyTracked) return false;
    if (item.isTrackingPresent || item.isTrackingFailed) return null;
    if (item?.trackingType === SHIPMENT_TYPE.HBL && (item?.isInfoFetchFailed || item?.isInfoPresent)) return null;
    return <div
      className={classes.loader}
      style = {{ color: '#51B841' }}
    >
      <Icon type="hourglass" />
      <span className={'loadingDots'}
        style={{ marginLeft: 8, color: '#51B841' }}>
        Fetching Data
      </span>
    </div>;
  };

  const renderErrorData = (forHBL?: boolean) => {
    return <div
      className={classes.loader}
      style={{ color: tokens.colors.error }}>
      <Icon
        type="info-circle"
        theme="filled"
      />
      <span
        style={{ marginLeft: 8, color: tokens.colors.error }}>
        {forHBL ? item.infoFetchFailureMessage : item.trackingFetchFailureMessage}
      </span>
    </div>;
  };


  const renderDefaultCard = () => {
    const {
      blNumber,
      hblNumber,
      bookingNumber,
      isManuallyTracked,
      isTrackingPresent,
      isInfoPresent,
    } = item;
    if (!isManuallyTracked && !(isTrackingPresent || isInfoPresent)) return null;
    return <React.Fragment>
      <Row className={classes.row}>
        <Col span={4}>
          <div className={classes.lastLocation}> BL# </div>
        </Col>
        <Col span={7}>
          <div className={classes.bookingNumber}>
            <Tooltip
              title={blNumber || ''}>
              {blNumber || '-'}
            </Tooltip>
          </div>
        </Col>
        <Col span={1}/>
        <Col span={4}>
          <div className={classes.lastLocation}>
            Last Location
          </div>
        </Col>
        <Col span={8}>
          <div className={classes.status}
            style={{
              wordBreak: 'break-word',
            }}>
            {isValidArray(item.lastLocation) ? item.lastLocation[0] : '-'}
          </div>
        </Col>
      </Row>
      <Row className={classes.row}>
        <Col span={4}>
          <div className={classes.lastLocation}> Booking# </div>
        </Col>
        <Col span={7}>
          <div className={classes.bookingNumber}>
            <Tooltip
              title={bookingNumber || ''}>
              {bookingNumber || '-'}
            </Tooltip>
          </div>
        </Col>
        <Col span={1} />
        <Col span={4}>
          <div className={classes.labelCol}>
            Days at Sea
          </div>
          </Col>
          <Col span={8}>
          <div className={classes.transitDays}>
            {getTransitDays()}
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <div className={classes.lastLocation}> Status </div>
          </Col>
          <Col span={7}>
          <div className={classes.status}>
            {item.status ? STATUS_LABELS[item.status] : item?.tag ? STATUS_LABELS[item.tag] : '-'}
          </div>
        </Col>
        <Col span={14}></Col>
      {/* <Col span={2}>{renderIcons()}</Col> */}
      </Row>
    </React.Fragment> ;
  };

  const renderDefaultCardForHBL = () => {
    const {
      hblNumber,
      isManuallyTracked,
      isTrackingPresent,
      isInfoPresent,
    } = item;
    if (!isManuallyTracked && !(isTrackingPresent || isInfoPresent)) return null;
    return <React.Fragment>
      <Row className={classes.row}>
      <Col span={4}>
          <div className={classes.labelCol}>
            Days at Sea
          </div>
          </Col>
          <Col span={8}>
          <div className={classes.transitDays}>
            {getTransitDays()}
          </div>
        </Col>
        <Col span={4}>
          <div className={classes.lastLocation}>
            Last Location
          </div>
        </Col>
        <Col span={8}>
          <div className={classes.status}
            style={{
              wordBreak: 'break-word',
            }}>
            {isValidArray(item.lastLocation) ? item.lastLocation[0] : '-'}
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <div className={classes.lastLocation}> Status </div>
          </Col>
          <Col span={7}>
          <div className={classes.status}>
            {item.status ? STATUS_LABELS[item.status] : item?.tag ? STATUS_LABELS[item.tag] : '-'}
          </div>
        </Col>
        <Col span={14}></Col>
      {/* <Col span={2}>{renderIcons()}</Col> */}
      </Row>
    </React.Fragment> ;
  };

  function getTransitDays() {
    if (!item.transitDays) return 'Not Available';
    if (!item.leftDays) return item.transitDays;
    const daysLeft = Math.abs(item.leftDays);
    const lateOrLeft = (item.leftDays > 0) ? 'Left' : 'Late';

    return `${item.transitDays} (${daysLeft} Days ${lateOrLeft})`;
  }

  const renderIcons = () => {
    if (
      container.isManuallyTracked ||
      isPublicLink ||
      container.blNumber ||
      container.bookingNumber
    ) return null;
    return <div className={classes.icons}>
      {match.params.shipmentType !== SHIPMENT_TYPE.BL &&
        <Icon
          type="delete"
          theme="filled"
          style={{
            fontSize: 12,
            cursor: 'pointer',
            marginRight: '12px',
          }}
          onClick={() => handleDelete(item)}
        />}
      <Icon
        type="edit"
        theme="filled"
        style={{
          fontSize: 12,
          cursor: 'pointer',
        }}
        onClick={() => handleEdit()}
      />
    </div>;
  };

  const renderDate = () => {
    if (!item.lastSuccessfulTrackingStatusUpdatedAt) return null;
    return <div >
      Updated:&nbsp;
      <ReactTimeAgo
        date={moment(item.lastSuccessfulTrackingStatusUpdatedAt).valueOf()} />
    </div>;
  };
  const renderVTButton = () => {
    return <div
      className={classes.flexRow}
      style={{
        justifyContent: 'flex-end',
        margin: '6px 0px 0px 0px',
      }}>
      {container?.isVesselTrackingPresent && <VTSideDrawer
      container={container}
      showButton={true}
      permissions={permissions}
      handleEdit={handleEdit}
      handleArchiveShipment={handleArchiveShipment}
      handleUnArchiveShipment={handleUnArchiveShipment}
      mainPageTabId={mainPageTabId}
      />}
    </div>;
  }
  const renderLastColumn = () => {
    return <div
      className={classes.flexRow}
      style={{
        marginTop: 2,
      }}>
    {isCardArchived() && renderCompletionReason()}
      <div style={{
        color: tokens.colors.textTertiary, fontSize: tokens.fontSize.small
      }}>
        {renderDate()}
      </div>
    </div>;
  };

  function isCardArchived(): boolean {
    return item && item.isArchived;
  }

  const toSnakeCaseSentence = (str: string) => {
    return str.split(' ').map(word => word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  const getCustomCompletionResponse = () => {
    if (item.completionReason !== "Empty Container returned" ||
        !(item.tracking) || 
        !(isValidArray(item.tracking)) ||
        (item.tracking.length === 0) ||
        item.tracking[item.tracking.length-1]?.formattedEventStr?.toLowerCase().includes("empty")
      ) return item.completionReason;

    return toSnakeCaseSentence(item.tracking[item.tracking.length-1]?.formattedEventStr || item.completionReason);
  };

  const renderCompletionReason = () => {
    if (!item.completionReason) return null;
    const reasonTxt = getCustomCompletionResponse();
    return (
      <div className={classes.completionReason}>
        <Icon type="info-circle" />
        {/* {item.completionReason} */}
        <Tooltip title={reasonTxt} placement='bottomLeft'>
          <span id="reason">{reasonTxt}</span>
        </Tooltip>
      </div>
    );
  };

  const renderDetails = () => {
    return <Row>
      <Col span={8}>
        {renderTag()}
        {renderContainerType()}
      </Col>
      <Col span={16}>
      <React.Fragment
        key={item.id}>
        {renderFailureMessage()}
        {renderFetchingData()}
        {renderTrackingHoldMessage()}
        {isHBL() ? renderDefaultCardForHBL() : renderDefaultCard()}
      </React.Fragment>
      </Col>
    </Row>;
  };

  const renderTag = () => {
    return <div className={classes.tag}>
      <strong style={{ maxWidth:'136px', wordWrap: 'break-word' }}>
        {item?.trackingType !== SHIPMENT_TYPE?.HBL ? item.containerNumber?.split('(')[0] || '' : item?.hblNumber}
      </strong>
      {item.tag && <Tag style={{
        borderColor: colorStatusMapping[item.tag],
        background: colorStatusMapping[item.tag],
        height: '23px',
      }}
        className={(item.tag &&
          (item.tag === 'DELAYED')) ? classes.error : classes.success}>
        {`${STATUS_LABELS[item.tag]}`}
      </Tag>}
    </div>;
  };

  const renderContainerType = () => {
    return <div className={classes.containerType}>
      {item.containerSize} {containerTypesKeyVal[item.containerType] || item.containerType}
    </div>;
  };

  const renderFailureMessage = () => {
    if (isHBL()) {
      if (item.isInfoPresent || !item.isInfoFetchFailed) return null;
      return renderErrorData(true);
    }
    if (item.isManuallyTracked) return false;
    if (item.isTrackingPresent || !item.isTrackingFailed) return null;
    return renderErrorData();
  };

  function handleClick() {
    onClick();
  }

  return <div className={
    classNames(
      classes.containerParent,
      item.isTrackingPresent ? classes.cursorStyle : null,
      isCardArchived() ? classes.archived : null,
    )}
    onClick={handleClick}
    style={{
      ...style,
    }}
    data-intercom-target={`${componentName}_${index}`}
  >
    {renderDetails()}
    {renderVTButton()}
    {renderLastColumn()}
  </div>;
};

const mapStateToProps = ({ staticData }) => ({
  containerTypesKeyVal: staticData.containerTypesKeyVal,
});

const SubContainerCardStyled = injectSheet(styles)(SubContainerCard);
export default withEximType(connect(mapStateToProps, null)(SubContainerCardStyled));
